import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useTheme } from '../../contexts/ThemeContext';
import logo from '../../components/Assets/logo2_250x250.png'
import {
  Sprout,
  DollarSign,
  Users,
  FileSpreadsheet,
  Shield,
  CheckCircle,
  ArrowRight,
  Sun,
  Moon,
  Cloud,
  HardDrive,
  Clock,
  Check,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LandingPage() {
  const { isDark, setTheme } = useTheme();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API_URL}/pricing`);
        setPricing(response.data);
      } catch (error) {
        // Use default pricing if API fails
        setPricing({
          trial: {
            name: "Free Trial",
            price: 0,
            duration: "7 days",
            features: ["Full access to all features", "Up to 10 workers", "Local storage only", "Email support"]
          },
          basic: {
            name: "Basic",
            price: 50,
            currency: "BZD",
            period: "month",
            features: ["Unlimited workers", "Local storage only", "Excel & PDF exports", "Email support", "SSB compliance"]
          },
          cloud: {
            name: "Cloud Save",
            price: 100,
            currency: "BZD",
            period: "month",
            features: ["Everything in Basic", "Cloud storage", "Multi-device access", "Priority support", "Data encryption", "Create sub access for workers"]
          }
        });
      }
    };
    fetchPricing();
  }, []);

  const features = [
    {
      icon: DollarSign,
      title: 'Automated Payroll',
      description: 'Enter cane weights, get instant pay calculations with SSB deductions.',
    },
    {
      icon: Users,
      title: 'Worker Management',
      description: 'Manage cane cutters, field personnel, trucks, and more.',
    },
    {
      icon: FileSpreadsheet,
      title: 'Excel & PDF Export',
      description: 'Export payroll with formulas for offline use and printing.',
    },
    {
      icon: Shield,
      title: 'SSB Compliant',
      description: 'Pre-configured Belize Social Security deduction brackets. Also generate SSB document to upload in the SSB portal.',
    },
  ];

  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="noise-overlay" />

      {/* Hero Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10 dark:opacity-5"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758614312118-4f7cd900ab26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzdWdhciUyMGNhbmUlMjBmaWVsZCUyMGJlbGl6ZXxlbnwwfHx8fDE3Njc5MDA5NDV8MA&ixlib=rb-4.1.0&q=85)'
        }}
      />

      {/* Navigation */}
      <nav className="relative z-10 glass-header">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 flex items-center justify-center">
              <img
                src={logo}
                alt="Cane Pay logo"
                className="w-full h-full object-contain scale-150"
              />
            </div>
            <span className="font-heading font-bold text-xl">Cane Pay</span>
          </div>
          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              data-testid="landing-theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <a href="#pricing">
              <Button variant="ghost">Pricing</Button>
            </a>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn">Sign in</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="register-nav-btn">Get Started</Button>
            </Link>
          </div>
          {/* Mobile hamburger */}
          <div className="flex items-center gap-2 sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </Button>
          </div>
        </div>
        {/* Mobile menu dropdown */}
        {menuOpen && (
          <div className="sm:hidden border-t border-border/50 px-4 py-3 flex flex-col gap-2">
            <a href="#pricing" onClick={() => setMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start">Pricing</Button>
            </a>
            <Link to="/login" onClick={() => setMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start" data-testid="login-nav-btn">Sign in</Button>
            </Link>
            <Link to="/register" onClick={() => setMenuOpen(false)}>
              <Button className="w-full" data-testid="register-nav-btn">Get Started</Button>
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 container mx-auto px-4 pt-20 pb-32">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in">
            <Clock className="w-4 h-4" />
            7-Day Free Trial • No Credit Card Required
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 animate-slide-up">
            Modern Payroll for
            <span className="block text-primary">Cane Farm Groups</span>
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 animate-slide-up" style={{ animationDelay: '100ms' }}>
            Replace paper-based payroll with automated calculations, SSB deductions,
            and professional exports. Enter weights, get instant net pay.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
            <Link to="/register">
              <Button size="lg" className="gap-2 px-8 h-14 text-base" data-testid="hero-get-started-btn">
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="gap-2 px-8 h-14 text-base" data-testid="hero-demo-btn">
                Try Demo Account
              </Button>
            </Link>
          </div>

          <p className="text-sm text-muted-foreground mt-4 animate-fade-in" style={{ animationDelay: '300ms' }}>
            Demo: demo@canepay.bz / demo123
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              A complete payroll solution designed specifically for cane farm operations in Belize.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="card-base p-6 hover:shadow-lg transition-shadow animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Start with a 7-day free trial. Choose the plan that fits your farm group.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Trial */}
            <Card className="card-base animate-slide-up" style={{ animationDelay: '0ms' }}>
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-muted-foreground" />
                </div>
                <CardTitle className="font-heading">{pricing?.trial?.name || 'Free Trial'}</CardTitle>
                <CardDescription>Try before you commit</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-4xl font-bold font-heading">Free</span>
                  <span className="text-muted-foreground ml-2">for 7 days</span>
                </div>
                <ul className="space-y-3 text-sm text-left">
                  {(pricing?.trial?.features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Link to="/register" className="w-full">
                  <Button variant="outline" className="w-full" data-testid="trial-signup-btn">
                    Start Free Trial
                  </Button>
                </Link>
              </CardFooter>
            </Card>

            {/* Basic Plan */}
            <Card className="card-base animate-slide-up" style={{ animationDelay: '100ms' }}>
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <HardDrive className="w-6 h-6 text-secondary" />
                </div>
                <CardTitle className="font-heading">{pricing?.basic?.name || 'Basic'}</CardTitle>
                <CardDescription>Local storage only</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-2xl sm:text-4xl font-bold font-heading">Contact us</span>
                </div>
                <ul className="space-y-3 text-sm text-left">
                  {(pricing?.basic?.features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Link to="/register" className="w-full">
                  <Button variant="outline" className="w-full" data-testid="basic-signup-btn">
                    Get Started
                  </Button>
                </Link>
              </CardFooter>
            </Card>

            {/* Cloud Plan - Featured */}
            <Card className="card-base ring-2 ring-primary shadow-lg animate-slide-up relative overflow-visible" style={{ animationDelay: '200ms' }}>
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Most Popular</Badge>
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Cloud className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="font-heading">{pricing?.cloud?.name || 'Cloud Save'}</CardTitle>
                <CardDescription>Cloud storage</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-2xl sm:text-4xl font-bold font-heading">Contact us</span>
                </div>
                <ul className="space-y-3 text-sm text-left">
                  {(pricing?.cloud?.features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Link to="/register" className="w-full">
                  <Button className="w-full" data-testid="cloud-signup-btn">
                    Get Started
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 py-20 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-6">
                Stop Calculating Manually
              </h2>
              <p className="text-muted-foreground mb-8">
                With Cane Pay, you only enter the raw data—cane weights and rates per delivery.
                The system automatically handles all calculations, deductions, and totals.
              </p>

              <ul className="space-y-4">
                {[
                  'Multiple delivery entries per worker',
                  'Different rates per delivery',
                  'Fixed SSB amounts (not percentages)',
                  'Immutable audit trail',
                  'Excel exports with all entries',
                  'Mobile-friendly interface',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="card-base p-6 lg:p-8">
                <div className="space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-border">
                    <span className="font-heading font-semibold">Payroll Preview</span>
                    <span className="status-badge status-draft">Draft</span>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">Carlos Rivera</p>
                        <p className="text-sm text-muted-foreground">3 deliveries</p>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>Jan 6 - TRK-101 @ $4.50/ton × 6.0 tons</span>
                          <span>$27.00</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Jan 7 - TRK-102 @ $4.95/ton × 5.5 tons</span>
                          <span>$27.23</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Jan 8 - TRK-101 @ $4.50/ton × 7.0 tons</span>
                          <span>$31.50</span>
                        </div>
                      </div>
                      <div className="flex justify-between mt-3 pt-2 border-t border-border/50">
                        <span className="text-sm">Gross: $85.73 | SSB: $2.45</span>
                        <span className="font-bold text-primary">Net: $83.28</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Net Pay</span>
                      <span className="font-heading font-bold text-2xl text-primary">BZD $83.28</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Ready to Modernize Your Payroll?
          </h2>
          <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">
            Join farm groups across Belize who have simplified their weekly payroll process.
            Start your 7-day free trial today.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="gap-2 px-8 h-14 text-base" data-testid="cta-get-started-btn">
              Start Free Trial
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <img
                  src={logo}
                  alt="Cane Pay logo"
                  className="w-full h-full object-contain scale-150"
                />
              </div>
              <span className="font-heading font-semibold">Cane Pay</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Cane Pay. Built for Belize.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
